import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ConnectionStatus } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { ModalController, Platform } from '@ionic/angular';
import { UsernameModalComponent } from './helpers/username-modal/username-modal.component';
import { AppService } from './services/app.service';
import { AuthenticationService } from './services/authentication.service';
import { DuelistsService } from './services/duelists.service';
import { NotificationService } from './services/notification.service';
import { OneSignalService } from './services/one-signal.service';
import { SocketService } from './services/socket.service';
import { ToastService } from './services/toast.service';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
	userIsAuthenticated: boolean = false;
	networkStatusOnline: ConnectionStatus = {
		connected: true,
		connectionType: 'none',
	};

	constructor(
		private authService: AuthenticationService,
		private duelistsService: DuelistsService,
		private platform: Platform,
		private router: Router,
		private zone: NgZone,
		private oneSignalService: OneSignalService,
		private notificationService: NotificationService,
		private modalCtrl: ModalController,
		private socketService: SocketService,
		private toastService: ToastService,
		private appService: AppService,
	) {
		this.initializeApp();
	}

	ngOnInit(): void {
		this.oneSignalService.initialize();
		this.appService.initialize();

		this.authService.authUser$.subscribe({
			next: authUser => {
				if (authUser) {
					// Connect to socket when user is authenticated
					this.socketService.connect();

					if (!authUser.username) {
						this.modalCtrl
							.create({
								component: UsernameModalComponent,
								backdropDismiss: false,
							})
							.then(modal => modal.present());
					}
					this.notificationService.getNotificationList();
				} else {
					// Disconnect socket when user logs out
					this.socketService.disconnect();
				}
			},
		});

		this.oneSignalService.notification$.subscribe({
			next: data => {
				if (data.type === 'closed') {
					this.authService.getUserProfile().subscribe();
				}
				if (data.type === 'accepted') {
					this.duelistsService.addDuelist(data.competitor);
				}
				this.notificationService.getNotificationList();
			},
		});

		this.socketService.duelCreated$.subscribe({
			next: data => {
				this.toastService.showToast(`Neues Duell von ${data.challengerUsername}!`, {
					text: 'Ansehen',
					handler: () => {
						const targetUrl = '/app/duel-detail/' + data.duelId;
						this.router.navigateByUrl(targetUrl, {
							replaceUrl: this.router.url.startsWith('/app/duel-detail/'),
						});
					},
				});
				this.notificationService.getNotificationList();
			},
		});

		this.socketService.duelAccepted$.subscribe({
			next: duelSecretId => {
				this.toastService.showToast(`Ein Duell wurde angenommen!`, {
					text: 'Ansehen',
					handler: () => {
						this.router.navigateByUrl('/app/duel-detail/' + duelSecretId, {
							replaceUrl: this.router.url.startsWith('/app/duel-detail/'),
						});
					},
				});
				this.notificationService.getNotificationList();
			},
		});

		this.socketService.duelUpdated$.subscribe({
			next: duelSecretId => {
				this.toastService.showToast(`Ein Duell wurde aktualisiert!`, {
					text: 'Ansehen',
					handler: () => {
						this.router.navigateByUrl('/app/duel-detail/' + duelSecretId, {
							replaceUrl: this.router.url.startsWith('/app/duel-detail/'),
						});
					},
				});
				this.notificationService.getNotificationList();
			},
		});
	}

	initializeApp() {
		this.platform.ready().then(() => {
			setTimeout(() => SplashScreen.hide(), 1000);

			// Fix Ionic not avoiding safe-areas (statusbars)
			// the bug can only happen on Android
			if (Capacitor.getPlatform() === 'android') {
				EdgeToEdge.enable();
			}
		});

		App.addListener('appUrlOpen', data => {
			this.zone.run(() => {
				const slug = data.url.split('.com').pop();
				if (slug) {
					// Comes from PayPal
					if (slug === '/app/profile') {
						this.authService.getUserProfile().subscribe();
					}
					this.router.navigateByUrl(slug);
				}
				// If no match, do nothing - let regular routing logic take over
			});
		});
	}
}
